import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const CuttingWaste = () => {
  // State variables for Cutting Waste calculation
  const [wasteFabricArea, setWasteFabricArea] = useState(0); // Waste fabric area
  const [totalFabricArea, setTotalFabricArea] = useState(0); // Total fabric area
  const [wasteFabricUnit, setWasteFabricUnit] = useState("m2"); // Unit for waste fabric area
  const [totalFabricUnit, setTotalFabricUnit] = useState("m2"); // Unit for total fabric area
  const [cuttingWastePercentage, setCuttingWastePercentage] = useState(0); // Calculated cutting waste percentage

  // Event handlers for input changes
  const handleWasteFabricAreaChange = (e) => setWasteFabricArea(parseFloat(e.target.value) || 0);
  const handleTotalFabricAreaChange = (e) => setTotalFabricArea(parseFloat(e.target.value) || 0);
  const handleWasteFabricUnitChange = (e) => setWasteFabricUnit(e.target.value);
  const handleTotalFabricUnitChange = (e) => setTotalFabricUnit(e.target.value);

  // useEffect hook to calculate Cutting Waste Percentage
  useEffect(() => {
    if (totalFabricArea > 0) {
      const percentage = (wasteFabricArea / totalFabricArea) * 100;
      setCuttingWastePercentage(percentage);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [wasteFabricArea, totalFabricArea]); // Recalculates when waste or total fabric area changes

  return (
    <Layout title="Cutting Waste Calculator" path="cutting-waste">
      <section className="section-padding" id="cutting-waste">
        <div className="container mt-5">
          <div className="row">
            {/* Cutting Waste Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Cutting Waste Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Waste Fabric Area */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="wasteFabricArea" className="form-label">
                        Waste Fabric Area
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Waste Fabric Area:</strong> The area of fabric that is wasted during the cutting process. This includes leftover pieces, cutting inefficiencies, and fabric that cannot be used."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="wasteFabricArea"
                        placeholder="Enter waste fabric area"
                        value={wasteFabricArea || ""}
                        onChange={handleWasteFabricAreaChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={wasteFabricUnit}
                        onChange={handleWasteFabricUnitChange}
                      >
                        <option value="m2">Square Meters (m²)</option>
                        <option value="ft2">Square Feet (ft²)</option>
                        <option value="yd2">Square Yards (yd²)</option>
                        <option value="cm2">Square Centimeters (cm²)</option>
                        <option value="mm2">Square Millimeters (mm²)</option>
                        <option value="in2">Square Inches (in²)</option>
                        <option value="acre">Acres (ac)</option>
                      </select>
                    </div>

                    {/* Input for Total Fabric Area */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalFabricArea" className="form-label">
                        Total Fabric Area
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Fabric Area:</strong> The total area of fabric used for the product, including all usable pieces and sections of the fabric."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalFabricArea"
                        placeholder="Enter total fabric area"
                        value={totalFabricArea || ""}
                        onChange={handleTotalFabricAreaChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={totalFabricUnit}
                        onChange={handleTotalFabricUnitChange}
                      >
                        <option value="m2">Square Meters (m²)</option>
                        <option value="ft2">Square Feet (ft²)</option>
                        <option value="yd2">Square Yards (yd²)</option>
                        <option value="cm2">Square Centimeters (cm²)</option>
                        <option value="mm2">Square Millimeters (mm²)</option>
                        <option value="in2">Square Inches (in²)</option>
                        <option value="acre">Acres (ac)</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Cutting Waste Percentage:{" "}
                        <span className="fw-bold ms-3">
                          {cuttingWastePercentage === 0
                            ? "0"
                            : cuttingWastePercentage.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Cutting Waste Calculation
                </h3>

                {/* What is Cutting Waste? */}
                <h4 className="mt-3 mb-3">What is Cutting Waste?</h4>
                <p>
                  **Cutting Waste** refers to the amount of fabric wasted during the cutting process. This can happen due to irregular shapes, misalignment, or inefficiencies in the cutting pattern.
                </p>

                {/* Why Use the Cutting Waste Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Cutting Waste Calculator?</h4>
                <p>
                  This calculator helps you calculate the percentage of fabric wasted during the cutting process, allowing you to take steps toward reducing waste and improving the efficiency of fabric usage.
                </p>

                {/* Benefits of Using the Cutting Waste Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Cutting Waste Calculator</h4>
                <ul>
                  <li>
                    <strong>Reduces Material Waste:</strong> Helps to identify areas where waste can be reduced.
                  </li>
                  <li>
                    <strong>Lowers Costs:</strong> Reduces the amount of fabric needed for production, cutting costs.
                  </li>
                  <li>
                    <strong>Increases Efficiency:</strong> Helps optimize fabric usage in cutting operations.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Cutting Waste Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Textile Mills:</strong> To monitor and reduce fabric waste during the cutting process.
                  </li>
                  <li>
                    <strong>Apparel Production:</strong> To optimize the use of fabric and minimize waste in garment manufacturing.
                  </li>
                  <li>
                    <strong>Cutting Teams:</strong> To ensure efficient fabric usage during the cutting phase of production.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">
                  How can reducing cutting waste benefit a textile factory?
                </h5>
                <p>
                  Reducing cutting waste helps a factory save on raw material costs and minimizes environmental impact, as less fabric needs to be purchased and disposed of. It also improves overall production efficiency.
                </p>

                <h5 className="mt-3 mb-2">Is this calculator useful for different fabric types?</h5>
                <p>
                  Yes, this calculator is useful for all fabric types as long as you have the necessary measurements for waste fabric area and total fabric area. Adjusting cutting techniques based on fabric characteristics can further reduce waste.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CuttingWaste;
